import { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import toast from "react-hot-toast";
import { EyeIcon, EyeOffIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import supabase from "../api/supabase";

const clearCachedUserProfile = () => {
  localStorage.removeItem("userProfile");
  localStorage.removeItem("userProfileExpiry");
};

const passwordSchema = z
  .object({
    password: z
      .string()
      .min(8, "Password must be at least 8 characters long")
      .regex(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
        "Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character"
      ),
    retypePassword: z.string(),
  })
  .refine((data) => data.password === data.retypePassword, {
    message: "Passwords do not match",
    path: ["retypePassword"],
  });

function Auth_PasswordReset() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showRetypePassword, setShowRetypePassword] = useState(false);
  const [isRecoverySession, setIsRecoverySession] = useState(false);
  const [isCheckingSession, setIsCheckingSession] = useState(true);

  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      password: "",
      retypePassword: "",
    },
  });

  useEffect(() => {
    let isMounted = true;
    let settled = false;

    const markReady = () => {
      if (!isMounted || settled) return;
      settled = true;
      setIsRecoverySession(true);
      setIsCheckingSession(false);
    };

    const fail = (message) => {
      if (!isMounted || settled) return;
      settled = true;
      setIsCheckingSession(false);
      clearCachedUserProfile();
      toast.error(message);
      navigate("/login");
    };

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        markReady();
      }
    });

    const establishRecoverySession = async () => {
      const hashParams = new URLSearchParams(
        window.location.hash.replace(/^#/, "")
      );
      const searchParams = new URLSearchParams(window.location.search);
      const accessToken = hashParams.get("access_token");
      const refreshToken = hashParams.get("refresh_token");
      const type = hashParams.get("type") || searchParams.get("type");
      const tokenHash =
        searchParams.get("token_hash") || searchParams.get("token");

      if (tokenHash) {
        const { data, error } = await supabase.auth.verifyOtp({
          token_hash: tokenHash,
          type: "recovery",
        });
        if (error || !data.session) {
          console.error("Failed to verify recovery token:", error?.message);
          fail("Invalid or expired reset link. Please request a new one.");
          return;
        }
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
        markReady();
        return;
      }

      if (accessToken && refreshToken) {
        const { data, error } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        });
        if (error || !data.session) {
          console.error("Failed to establish recovery session:", error?.message);
          fail("Invalid or expired reset link. Please request a new one.");
          return;
        }
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
        markReady();
        return;
      }

      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (session && type === "recovery") {
        markReady();
        return;
      }

      await new Promise((resolve) => setTimeout(resolve, 800));
      if (!settled) {
        fail("Invalid or missing reset link. Request a new one.");
      }
    };

    establishRecoverySession();

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [navigate]);

  const onSubmit = async (values) => {
    setIsSubmitting(true);

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session) {
        clearCachedUserProfile();
        toast.error("Reset session expired. Please request a new reset link.");
        navigate("/login");
        return;
      }

      const { error } = await supabase.auth.updateUser({
        password: values.password,
      });

      if (error) {
        console.error("Error resetting password:", error.message, error);
        toast.error(error.message || "Password reset failed. Please try again.");
        return;
      }

      toast.success("Password reset successful! Redirecting to login...");
      reset();
      await supabase.auth.signOut();
      clearCachedUserProfile();
      navigate("/login");
    } catch (err) {
      console.error("Error during password reset:", err);
      toast.error(err?.message || "Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const shell = (content) => (
    <div className="flex h-screen items-center justify-center bg-background">
      <div className="w-full max-w-[400px] space-y-6 rounded-lg bg-white p-8 shadow-lg m-4">
        <div className="space-y-2 text-center">
          <div className="flex justify-center mb-4">
            <img
              src="/puritylogo_main.png"
              alt="Purity Logo"
              className="w-40 h-auto"
            />
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Password Reset
          </h1>
          <p className="text-sm text-muted-foreground">
            {isCheckingSession
              ? "Verifying your reset link..."
              : "Enter your new password below"}
          </p>
        </div>
        {content}
      </div>
    </div>
  );

  if (isCheckingSession) {
    return shell(
      <p className="text-center text-sm text-muted-foreground">Please wait...</p>
    );
  }

  if (!isRecoverySession) return null;

  return shell(
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="space-y-2">
          <label
            htmlFor="password"
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            New Password
          </label>
          <div className="relative">
            <Controller
              name="password"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  id="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  className={errors.password ? "border-red-500" : undefined}
                />
              )}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? (
                <EyeOffIcon className="h-4 w-4 text-gray-500" />
              ) : (
                <EyeIcon className="h-4 w-4 text-gray-500" />
              )}
            </button>
          </div>
          {errors.password && (
            <p className="text-sm text-red-500">{errors.password.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <label
            htmlFor="retypePassword"
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            Retype Password
          </label>
          <div className="relative">
            <Controller
              name="retypePassword"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  id="retypePassword"
                  type={showRetypePassword ? "text" : "password"}
                  autoComplete="new-password"
                  className={
                    errors.retypePassword ? "border-red-500" : undefined
                  }
                />
              )}
            />
            <button
              type="button"
              onClick={() => setShowRetypePassword(!showRetypePassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2"
              aria-label={
                showRetypePassword ? "Hide password" : "Show password"
              }
            >
              {showRetypePassword ? (
                <EyeOffIcon className="h-4 w-4 text-gray-500" />
              ) : (
                <EyeIcon className="h-4 w-4 text-gray-500" />
              )}
            </button>
          </div>
          {errors.retypePassword && (
            <p className="text-sm text-red-500">
              {errors.retypePassword.message}
            </p>
          )}
        </div>

        <Button type="submit" className="w-full" disabled={isSubmitting}>
          {isSubmitting ? "Saving..." : "Save"}
        </Button>
      </form>

      <div className="text-center text-sm">
        <button
          type="button"
          onClick={() => navigate("/login")}
          className="text-primary hover:underline"
        >
          Back to login
        </button>
      </div>
    </>
  );
}

export default Auth_PasswordReset;
